import { Box, Grid, MenuItem, TextField, Typography } from '@material-ui/core'
import { useFormik } from 'formik'
import React from 'react'
import DatePicker from 'UI/DatePicker/DatePicker'
import {
  CategoriesEligibleForRejection,
  DealResolvableCategories,
  DealTransactionTypes,
  ResolvedTransactionTypes,
  StatusPayment,
} from 'typescript/enums/transactions'
import { IDealPaymentFormValues } from 'typescript/interfaces/deals'
import Button from 'UI/Button/ButtonWithPreloader'
import SelectComponent from 'UI/Select'
import FormControlLabel from '@material-ui/core/FormControlLabel/FormControlLabel'
import Checkbox from 'UI/Checkbox'
import { IBankAccountClient } from 'typescript/interfaces/clients'
import { schemaDealPayment, schemaDealPaymentResolveMode } from 'components/DealPaymentForm/schema'
import RequiredOption from 'UI/Select/RequiredOption'
import isValidDate from 'utils/isValidDate'
import convertDateForPicker from 'utils/convertDateForPicker'
import VendorAutoComplete from 'components/VendorAutoComplete'
import DecimalTextField from 'UI/TextField/DecimalTextField'
import { REJECTION_CODES, REJECTION_CODE_CATEGORY, REJECTION_CODE_DIRECTION } from 'constants/rejectionCodes'

interface IDealPaymentFormProps {
  cancel: () => void
  submit: (values: IDealPaymentFormValues) => void
  loading?: boolean
  bankAccounts: IBankAccountClient[]
  editMode?: boolean
  resolveMode?: boolean
  initialValues?: IDealPaymentFormValues
}

export const isRejectionCodeApplicable = (values: IDealPaymentFormValues) =>
  values.status === StatusPayment.rejected &&
  values.category === REJECTION_CODE_CATEGORY &&
  values.direction === REJECTION_CODE_DIRECTION

const defaultInitValues: IDealPaymentFormValues = {
  scheduledFor: '',
  amount: undefined,
  achProvider: '',
  bankAccountId: '',
  status: '',
  category: '',
  paymentDate: '',
  comments: '',
  vendorId: '',
  resolved: null,
  resolvePayment: false,
  rejectionCode: '',
}

const DealPaymentForm = ({
  submit,
  cancel,
  loading,
  bankAccounts,
  editMode,
  resolveMode,
  initialValues = defaultInitValues,
}: IDealPaymentFormProps) => {
  const { handleSubmit, values, errors, touched, handleChange, setFieldValue } = useFormik<IDealPaymentFormValues>({
    initialValues: {
      ...initialValues,
      bankAccountId: bankAccounts.find((bankAcc) => bankAcc.isMain)?.bankAccount.id || '',
    },
    enableReinitialize: true,
    validationSchema: resolveMode ? schemaDealPaymentResolveMode : schemaDealPayment,
    onSubmit: formValues =>
      submit({
        ...formValues,
        rejectionCode: isRejectionCodeApplicable(formValues) ? formValues.rejectionCode : undefined,
      }),
  })

  const showRejectionCode = editMode && isRejectionCodeApplicable(values)

  const handleRejectedStatusChange = (e: React.ChangeEvent<any>) => {
    const { value } = e.target
    if (value === StatusPayment.rejected && values.status !== StatusPayment.rejected) setFieldValue('comments', '')
    handleChange(e)
  }

  const handleCategoryChange = (e: React.ChangeEvent<any>) => {
    handleChange(e)
    if (!DealResolvableCategories.includes(e.target.value)) setFieldValue('resolvePayment', false)
  }

  const renderCommentLabel = values.status !== StatusPayment.rejected 
    ? 'Comment'
    : values.category === 'Payment ACH'
      ? 'Reason of Rejection'
      : <RequiredOption label="Reason of Rejection" />

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <DatePicker
            fullWidth
            label={<RequiredOption label="Scheduled For" />}
            name="scheduledFor"
            value={values.scheduledFor || null}
            error={Boolean(touched.scheduledFor && errors.scheduledFor) || !isValidDate(values.scheduledFor)}
            helperText={(touched.scheduledFor && errors.scheduledFor) || (!isValidDate(values.scheduledFor) && 'MM/DD/YYYY')}
            type="keyboard"
            onChange={(date) => setFieldValue('scheduledFor', convertDateForPicker(date))}
          />
        </Grid>
        {!editMode && (
          <Grid item xs={12}>
            <DecimalTextField
              disabled={editMode || resolveMode}
              fullWidth
              variant="outlined"
              label={<RequiredOption label="Amount" />}
              name="amount"
              value={values.amount}
              onChange={handleChange}
              error={Boolean(touched.amount && errors.amount)}
              helperText={touched.amount && errors.amount}
            />
          </Grid>
        )}
        {/*<Grid item xs={12}>*/}
        {/*  <TextField*/}
        {/*    fullWidth*/}
        {/*    variant="outlined"*/}
        {/*    label={<RequiredOption label="Ach Provider"/>}*/}
        {/*    name="achProvider"*/}
        {/*    value={values.achProvider}*/}
        {/*    onChange={handleChange}*/}
        {/*    error={Boolean(touched.achProvider && errors.achProvider)}*/}
        {/*    helperText={touched.achProvider && errors.achProvider}*/}
        {/*  />*/}
        {/*</Grid>*/}
        {!editMode && (
          <Grid item xs={12}>
            <SelectComponent
              disabled={editMode}
              value={values.bankAccountId}
              name="bankAccountId"
              label={<RequiredOption label="Bank Account" />}
              onChange={handleChange}
              error={Boolean(touched.bankAccountId && errors.bankAccountId)}
              helperText={touched.bankAccountId && errors.bankAccountId}
            >
              {bankAccounts.map((bankAcc) => (
                <MenuItem value={bankAcc.bankAccount.id}>{bankAcc.bankAccount.bankName}</MenuItem>
              ))}
            </SelectComponent>
          </Grid>
        )}
        <Grid item xs={12}>
          {editMode ? (
            <SelectComponent
              value={values.status}
              name="status"
              label={<RequiredOption label="Payment Status" />}
              onChange={handleRejectedStatusChange}
              error={Boolean(touched.status && errors.status)}
              helperText={touched.status && errors.status}
            >
              <MenuItem value="ProcessingBatch">{StatusPayment.processingBatch}</MenuItem>
              <MenuItem value="Scheduled">{StatusPayment.scheduled}</MenuItem>
              <MenuItem value="Successful">{StatusPayment.successful}</MenuItem>
              <MenuItem value="Cancelled">{StatusPayment.cancelled}</MenuItem>
              {Object.values(CategoriesEligibleForRejection).includes(values.category ?? '') && values.direction === 'Debit' && (
                <MenuItem value="Rejected">{StatusPayment.rejected}</MenuItem>
              )}
            </SelectComponent>
          ) : (
            <SelectComponent
              disabled={resolveMode}
              value={values.status}
              name="status"
              label={<RequiredOption label="Payment Status" />}
              onChange={handleChange}
              error={Boolean(touched.status && errors.status)}
              helperText={touched.status && errors.status}
            >
              <MenuItem value="Scheduled">{StatusPayment.scheduled}</MenuItem>
              <MenuItem value="Successful">{StatusPayment.successful}</MenuItem>
            </SelectComponent>
          )}
        </Grid>

        {!editMode && (
          <Grid item xs={12}>
            <SelectComponent
              disabled={editMode}
              value={values.category}
              name="category"
              label={<RequiredOption label="Transaction Type" />}
              onChange={handleCategoryChange}
              error={Boolean(touched.category && errors.category)}
              helperText={touched.category && errors.category}
            >
              {Object.entries(resolveMode ? ResolvedTransactionTypes : DealTransactionTypes).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </SelectComponent>
          </Grid>
        )}
        {!editMode && values.category === 'LegalCostVendor' && (
          <Grid item xs={12}>
            <VendorAutoComplete
              vendorId={values.vendorId}
              error={Boolean(touched.vendorId && errors.vendorId)}
              helperText={touched.vendorId && errors.vendorId}
              onChange={(e, value) => {
                setFieldValue('vendorId', value?.id ?? '')
              }}
            />
          </Grid>
        )}
        {!editMode && !resolveMode && DealResolvableCategories.includes(values.category ?? '') && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="resolvePayment"
                  checked={!!values.resolvePayment}
                  color="primary"
                  onChange={(e) => setFieldValue('resolvePayment', e.target.checked)}
                />
              }
              label={<Typography color="textSecondary">Resolve Payment</Typography>}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <DatePicker
            fullWidth
            label={resolveMode ? <RequiredOption label="Payment Date" /> : 'Payment Date'}
            name="paymentDate"
            value={values.paymentDate || null}
            error={Boolean(touched.paymentDate && errors.paymentDate) || !isValidDate(values.paymentDate)}
            helperText={(touched.paymentDate && errors.paymentDate) || (!isValidDate(values.paymentDate) && 'MM/DD/YYYY')}
            type="keyboard"
            onChange={(date) => setFieldValue('paymentDate', convertDateForPicker(date))}
          />
        </Grid>
        {showRejectionCode && (
          <Grid item xs={12}>
            <SelectComponent
              value={values.rejectionCode}
              name="rejectionCode"
              label={<RequiredOption label="Rejection Code" />}
              onChange={handleChange}
              error={Boolean(touched.rejectionCode && errors.rejectionCode)}
              helperText={touched.rejectionCode && errors.rejectionCode}
            >
              {Object.entries(REJECTION_CODES).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </SelectComponent>
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField
            fullWidth
            variant="outlined"
            label={renderCommentLabel}
            name="comments"
            multiline
            rows={3}
            value={values.comments}
            onChange={handleChange}
            error={Boolean(touched.comments && errors.comments)}
            helperText={touched.comments && errors.comments}
          />
        </Grid>
        {values.resolved && (
          <Box mb={2}>
            <Typography color="error">You cannot edit a resolved payment.</Typography>
          </Box>
        )}
      </Grid>
      <Box mt={10}>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <Button onClick={cancel} fullWidth color="secondary" variant="contained">
              Cancel
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button loading={loading} disabled={editMode && !!values.resolved} fullWidth color="primary" variant="contained" type="submit">
              Save
            </Button>
          </Grid>
        </Grid>
      </Box>
    </form>
  )
}

export default DealPaymentForm
